import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { Image, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { searchBelgianCompany } from "@/utils/company-search";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { COMPANY_RELATIONSHIPS, CompanyRelationship, getRelationshipLabel } from "@/utils/companyRelationship";

interface BasicInformationProps {
  name: string;
  vatNumber: string;
  assignee: string;
  websiteUrl?: string;
  logoUrl?: string;
  relationship?: CompanyRelationship;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoChange?: (logoUrl: string | undefined) => void;
}

interface UserInfo {
  user_id: string;
  first_name: string;
  last_name: string;
  active: boolean;
}

export const BasicInformation = ({
  name,
  vatNumber,
  assignee,
  websiteUrl,
  logoUrl,
  relationship = 'none',
  onChange,
  onLogoChange
}: BasicInformationProps) => {
  const { t } = useTranslation();
  const [isSearchingLogo, setIsSearchingLogo] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoResults, setLogoResults] = useState<{ brandId: string; name: string; icon: string; }[]>([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);
  const { currentOrganization } = useAuth();
  const [orgUsers, setOrgUsers] = useState<UserInfo[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchOrgUsers = async () => {
      if (!currentOrganization?.id) return;
      
      try {
        // Use the database function that provides full access to user names
        const { data, error } = await supabase
          .rpc('get_organization_users_for_assignee', {
            p_organization_uuid: currentOrganization.id
          });
        
        if (error) {
          console.error("Error fetching organization users:", error);
          setFetchError(error.message || "Failed to load users");
        } else if (data) {
          setOrgUsers(data);
        }
      } catch (error: any) {
        console.error("Error fetching organization users:", error);
        setFetchError(error.message || "Failed to load users");
      }
    };
    
    fetchOrgUsers();
  }, [currentOrganization?.id]);
  
  const handleLogoRemove = () => {
    if (onLogoChange) {
      onLogoChange(undefined);
    }
  };
  
  const searchLogo = async () => {
    if (!name && !vatNumber) {
      toast({
        title: "Missing Information",
        description: "A company name or VAT number is required to search for a logo.",
        variant: "destructive",
      });
      return;
    }

    setIsSearchingLogo(true);
    try {
      // Search using company name or VAT number
      const searchTerm = name || vatNumber;
      const result = await searchBelgianCompany(searchTerm);
      
      if (!result || result.logos.length === 0) {
        toast({
          title: "Logo Not Found",
          description: "No logo was found for this company.",
        });
        setIsSearchingLogo(false);
        return;
      }

      // Open dialog with logo options
      setLogoResults(result.logos);
      setIsLogoDialogOpen(true);
    } catch (error) {
      console.error("Error searching for logo:", error);
      toast({
        title: "Search Failed",
        description: "Failed to search for company logo.",
        variant: "destructive",
      });
    } finally {
      setIsSearchingLogo(false);
    }
  };

  const confirmLogoSelection = () => {
    if (selectedLogoUrl && onLogoChange) {
      onLogoChange(selectedLogoUrl);
      setIsLogoDialogOpen(false);
      setSelectedLogoUrl(null);
    }
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.createModal.nameLabel")} <span className="text-red-500">*</span>
        </label>
        <Input
          id="name"
          value={name}
          onChange={onChange}
          required
        />
      </div>
      
      <div>
        <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.createModal.vatNumberLabel")}
        </label>
        <Input
          id="vat_number"
          value={vatNumber}
          onChange={onChange}
          placeholder="BE0123456789"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Belgian VAT format: BE0123456789</p>
      </div>
      
      <div>
        <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.createModal.websiteUrlLabel", "Company URL")}
        </label>
        <Input
          id="website_url"
          value={websiteUrl || ""}
          onChange={onChange}
          placeholder="www.example.com"
          type="text"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter domain (e.g., www.belfius.be or belfius.be)</p>
      </div>
      
      <div>
        <label htmlFor="assignee" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.createModal.assigneeLabel")}
        </label>
        <select
          id="assignee"
          value={assignee}
          onChange={e => onChange({
            target: { id: "assignee", value: e.target.value }
          } as React.ChangeEvent<HTMLInputElement>)}
          className="block w-full border border-gray-300 dark:border-gray-600 rounded-sm px-3 py-2 text-sm bg-transparent"
          required={orgUsers.length > 0}
        >
          <option value="">{t("companies.createModal.assigneePlaceholder", "Select an assignee")}</option>
          {orgUsers.length === 0 && (
            <option value="" disabled>{t("companies.createModal.noAssignees", "No users found in your company")}</option>
          )}
          {orgUsers.filter(user => user.active !== false).map(user => (
            <option key={user.user_id} value={user.user_id}>
              {user.first_name} {user.last_name}
            </option>
          ))}
        </select>
        {fetchError && (
          <p className="text-xs text-amber-600 mt-1">
            {fetchError}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.relationship.label")}
        </label>
        <select
          id="relationship"
          value={relationship}
          onChange={e => onChange({
            target: { id: "relationship", value: e.target.value }
          } as React.ChangeEvent<HTMLInputElement>)}
          className="block w-full border border-gray-300 dark:border-gray-600 rounded-sm px-3 py-2 text-sm bg-transparent"
        >
          {COMPANY_RELATIONSHIPS.map(rel => (
            <option key={rel} value={rel}>{getRelationshipLabel(rel, t)}</option>
          ))}
        </select>
      </div>

      {/* Logo Preview Section */}
      <div className="md:col-span-2 mt-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("companies.createModal.logoLabel", "Company Logo")}
        </label>
        <div className="flex items-start space-x-4">
          {logoUrl ? (
            <div className="relative border rounded-sm p-2 bg-white dark:bg-gray-800 w-20 h-20 flex items-center justify-center">
              <img src={logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain" />
              <Button 
                variant="outline" 
                size="icon" 
                className="absolute -top-2 -right-2 h-6 w-6 rounded-sm bg-white dark:bg-gray-800" 
                onClick={searchLogo}
                disabled={isSearchingLogo}
                title={t("companies.search.searchForLogo", "Search for company logo")}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="relative border rounded-sm p-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center flex-col w-20 h-20">
              <Image className="h-8 w-8 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("companies.createModal.noLogo", "No Logo")}</span>
              <Button 
                variant="outline" 
                size="icon" 
                className="absolute -top-2 -right-2 h-6 w-6 rounded-sm bg-white dark:bg-gray-800" 
                onClick={searchLogo}
                disabled={isSearchingLogo}
                title={t("companies.search.searchForLogo", "Search for company logo")}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {logoUrl && (
            <button 
              type="button" 
              onClick={handleLogoRemove}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              {t("companies.createModal.removeLogo", "Remove Logo")}
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t("companies.createModal.logoSearchHelp", "Click the search icon to find logos automatically")}
        </p>
      </div>

      {/* Logo Search Dialog */}
      <Dialog open={isLogoDialogOpen} onOpenChange={setIsLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("companies.search.selectLogo", "Select Company Logo")}</DialogTitle>
            <DialogDescription>
              {t("companies.search.selectLogoDescription", "Choose a logo for this company")}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-60 my-4">
            <RadioGroup
              value={selectedLogoUrl || ""}
              onValueChange={setSelectedLogoUrl}
              className="grid grid-cols-2 sm:grid-cols-3 gap-3"
            >
              {logoResults.map((logo) => (
                <Label
                  key={logo.brandId}
                  htmlFor={`logo-${logo.brandId}`}
                  className={`relative flex flex-col items-center justify-center p-2 border rounded-sm cursor-pointer hover:border-digger-blue ${selectedLogoUrl === logo.icon ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                >
                  <RadioGroupItem value={logo.icon} id={`logo-${logo.brandId}`} className="sr-only" />
                  <img src={logo.icon} alt={`${logo.name} logo`} className="h-12 w-12 object-contain mb-1" />
                  <span className="text-xs text-center truncate w-full">{logo.name}</span>
                </Label>
              ))}
            </RadioGroup>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsLogoDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={confirmLogoSelection} disabled={!selectedLogoUrl}>
              {t("common.confirm", "Confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
